/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, OnDestroy, OnInit, HostListener} from '@angular/core';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {Subject} from 'rxjs';
import * as moment from 'moment';
import {CheckListEntityFinancials} from '../../../../../model';
import {FinancialType} from '../../../../../model/enums/financial.type';
import {CheckListFinancialsSetup} from '../../../../../model/checklist';

@Component({
  selector: 'checklist-financials-modal',
  templateUrl: './checklist-financials.modal.html',
  styleUrls: ['./checklist-financials.modal.scss']
})
export class ChecklistFinancialsModal implements OnInit, OnDestroy {
  subject: Subject<CheckListFinancialsSetup>;
  subscription: any;

  NOT_DEC: boolean;
  FYE_DATE: Date = moment().subtract(1, 'months').endOf('month').toDate();
  INT_DATE: Date = moment().subtract(1, 'months').endOf('month').toDate();

  FYE_SELECTED = false;
  INT_SELECTED = false;

  INTERIM = false;

  constructor(
    public bsModalRef: BsModalRef,
    private modalService: BsModalService
  ) {}

  ngOnInit(): void {
    // Fix for when user clicks `ESC` instead of clicking, no Event was firing
    this.subscription = this.modalService.onHide.subscribe((reason: string) => {
      if (reason === 'esc')
        this.onCancel();
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
  }

  onConfirm(): void {
    this.createStructure();
  }

  onCancel(): void {
    this.bsModalRef.hide();
    this.subject.next(null);
    this.subject.complete();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent):void {
    if (this.INT_SELECTED === true) {
      const x = event.keyCode;
      if (x === 13) {
        this.createStructure();
      }
    }
  }

  private createStructure(): void {
    // Clear Time on Interim Date
    this.INT_DATE.setHours(0, 0, 0, 0);

    // Setting Dates
    if (!this.NOT_DEC) { // If FYE is 31 December
      this.FYE_DATE = moment(this.INT_DATE).month(11).date(31).toDate();
    } else { // If FYE NOT 31 December
      if(moment(this.FYE_DATE).month() === 1 && moment(this.FYE_DATE).date() === 29)
        this.FYE_DATE =  moment(this.FYE_DATE).subtract(1, 'days').toDate();
      this.FYE_DATE.setFullYear(moment(this.INT_DATE).year());
    }

    // Start Year
    let startYear = moment(this.INT_DATE).year();

    // Checking if Interim
    if(moment(this.FYE_DATE).format('MM/DD/YYYY') !== moment(this.INT_DATE).format('MM/DD/YYYY')) {
      this.INTERIM = true;

      if (this.INT_DATE.getTime() > this.FYE_DATE.getTime())
        startYear = startYear + 1;
    }else this.INTERIM = false;

    // Result
    const result = new CheckListFinancialsSetup();
    result.NOT_DEC = this.NOT_DEC;
    result.INTERIM = this.INTERIM;
    result.FYE_DATE = this.FYE_DATE;
    result.INT_DATE = this.INT_DATE;

    result.DATA = [
      CheckListEntityFinancials.createDefault(startYear - 3, this.getFyeDate(startYear - 3), FinancialType.FEDERAL_TAX_RETURN),
      CheckListEntityFinancials.createDefault(startYear - 2, this.getFyeDate(startYear - 2), FinancialType.FEDERAL_TAX_RETURN),
      CheckListEntityFinancials.createDefault(startYear - 1, this.getFyeDate(startYear - 1), FinancialType.FEDERAL_TAX_RETURN)
    ];

    if (this.INTERIM)
      result.DATA.push(CheckListEntityFinancials.createDefaultInterim(moment(this.INT_DATE).get('year'), moment(this.INT_DATE).format('YYYY-MM-DD'), FinancialType.INTERNAL, true));
    else result.DATA.push(CheckListEntityFinancials.createDefault(startYear, this.getFyeDate(startYear), FinancialType.FEDERAL_TAX_RETURN));

    this.bsModalRef.hide();
    this.subject.next(result);
    this.subject.complete();
  }

  getFyeDate(yr: number): string {
    let FYE = this.FYE_DATE;
    if (!this.NOT_DEC)
      FYE = moment(this.INT_DATE).month(11).date(31).toDate();
    return moment(FYE).year(yr).format('YYYY-MM-DD');
  }

  get thisYearDecFye(): Date {
    return moment('31-12', 'DD-MM').toDate();
  }
}
